import { Add, DeleteOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    type ComparisonOperator,
    type VisualCondition,
    type VisualRule,
    UNARY_OPERATORS,
    compileRule,
    decompileRule,
} from '../condition-mapper';
import type { FieldCondition } from '../product-requirements-config.dto';

export interface FieldOption {
    code: string;
    label: string;
}

interface Props {
    title: string;
    /** Current json-logic rule (null = no rule). */
    value: FieldCondition | null;
    /** Sibling fields the condition may reference. */
    fieldOptions: FieldOption[];
    onChange: (value: FieldCondition | null) => void;
    helperText?: string;
}

const OPERATOR_LABELS: Record<ComparisonOperator, string> = {
    equals: 'is exactly',
    not_equals: 'is not',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
    gt: 'is greater than',
    gte: 'is greater than or equal to',
    lt: 'is less than',
    lte: 'is less than or equal to',
    includes: 'includes',
    count_gt: 'entry count is greater than',
    count_gte: 'entry count is greater than or equal to',
    count_eq: 'entry count equals',
    count_lt: 'entry count is less than',
    count_lte: 'entry count is less than or equal to',
};

const ALL_OPERATORS = Object.keys(OPERATOR_LABELS) as ComparisonOperator[];

export default function ConditionBuilder({ title, value, fieldOptions, onChange, helperText }: Props) {
    const { t } = useTranslation();

    // Seed once from the incoming value via lazy initializers (deliberately NOT
    // re-derived when `value` changes — edits round-trip through onChange and
    // must not fight the local state). A non-null value that doesn't decompile
    // is a hand-authored rule: `advanced` shows it read-only and preserves it.
    const [seed] = useState(() => (value == null ? null : decompileRule(value)));
    const [rule, setRule] = useState<VisualRule | null>(seed);
    const [advanced, setAdvanced] = useState(value != null && seed == null);

    function emit(next: VisualRule | null) {
        setRule(next);
        onChange(next ? compileRule(next) : null);
    }

    if (advanced) {
        return (
            <Box>
                <Typography variant="subtitle2">{title}</Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                    {t('This rule is too advanced to edit visually and will be kept as-is.')}
                </Alert>
                <TextField
                    fullWidth
                    multiline
                    size="small"
                    margin="dense"
                    value={JSON.stringify(value, null, 2)}
                    slotProps={{ input: { readOnly: true } }}
                />
                <Button
                    size="small"
                    color="warning"
                    onClick={() => {
                        setAdvanced(false);
                        emit({ mode: 'all', conditions: [] });
                    }}
                >
                    {t('Replace with a visual rule')}
                </Button>
            </Box>
        );
    }

    if (rule == null) {
        return (
            <Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="subtitle2">{title}</Typography>
                    <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => emit({ mode: 'all', conditions: [blankCondition(fieldOptions)] })}
                    >
                        {t('Add rule')}
                    </Button>
                </Stack>
                {helperText && (
                    <Typography variant="caption" color="text.secondary">
                        {helperText}
                    </Typography>
                )}
            </Box>
        );
    }

    function updateCondition(index: number, patch: Partial<VisualCondition>) {
        const conditions = rule!.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c));
        emit({ ...rule!, conditions });
    }

    function removeCondition(index: number) {
        const conditions = rule!.conditions.filter((_, i) => i !== index);
        emit(conditions.length === 0 ? null : { ...rule!, conditions });
    }

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">{title}</Typography>
                {rule.conditions.length > 1 && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>{t('Match')}</InputLabel>
                        <Select
                            label={t('Match')}
                            value={rule.mode}
                            onChange={(e) => emit({ ...rule, mode: e.target.value as 'all' | 'any' })}
                        >
                            <MenuItem value="all">{t('All conditions')}</MenuItem>
                            <MenuItem value="any">{t('Any condition')}</MenuItem>
                        </Select>
                    </FormControl>
                )}
                <Button size="small" color="error" onClick={() => emit(null)}>
                    {t('Remove rule')}
                </Button>
            </Stack>

            <Stack spacing={1}>
                {rule.conditions.map((condition, index) => (
                    <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>{t('Field')}</InputLabel>
                            <Select
                                label={t('Field')}
                                value={condition.field}
                                onChange={(e) => updateCondition(index, { field: e.target.value })}
                            >
                                {fieldOptions.map((f) => (
                                    <MenuItem key={f.code} value={f.code}>
                                        {f.label} ({f.code})
                                    </MenuItem>
                                ))}
                                {/* Keep a stale reference visible rather than blanking it. */}
                                {condition.field && !fieldOptions.some((f) => f.code === condition.field) && (
                                    <MenuItem value={condition.field}>{condition.field}</MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>{t('Condition')}</InputLabel>
                            <Select
                                label={t('Condition')}
                                value={condition.operator}
                                onChange={(e) =>
                                    updateCondition(index, { operator: e.target.value as ComparisonOperator })
                                }
                            >
                                {ALL_OPERATORS.map((op) => (
                                    <MenuItem key={op} value={op}>
                                        {t(OPERATOR_LABELS[op])}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {!UNARY_OPERATORS.has(condition.operator) && (
                            <TextField
                                size="small"
                                label={t('Value')}
                                value={String(condition.value ?? '')}
                                onChange={(e) => updateCondition(index, { value: e.target.value })}
                            />
                        )}

                        <IconButton
                            size="small"
                            onClick={() => removeCondition(index)}
                            aria-label={t('Remove condition')}
                        >
                            <DeleteOutlined fontSize="small" />
                        </IconButton>
                    </Stack>
                ))}

                <Box>
                    <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() =>
                            emit({ ...rule, conditions: [...rule.conditions, blankCondition(fieldOptions)] })
                        }
                    >
                        {t('Add condition')}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
}

function blankCondition(fieldOptions: FieldOption[]): VisualCondition {
    return { field: fieldOptions[0]?.code ?? '', operator: 'equals', value: '' };
}
